import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { cms } from '../i18n/localize';

interface LeverageSectionProps {
  badge?: string;
  title?: string;
  description?: string;
  leftImageUrl?: string;
  rightImageUrl?: string;
  leftTitle?: string;
  leftDescription?: string;
  rightTitle?: string;
  rightDescription?: string;
  beforeLabel?: string;
  afterLabel?: string;
}

const DEFAULT_LEFT_IMAGE =
  'https://static.wixstatic.com/media/6ff1e1_1c85ec594a994be88f8f525f2808bd79~mv2.png/v1/fill/w_716,h_402,al_c,lg_1,q_85,enc_avif,quality_auto/6ff1e1_1c85ec594a994be88f8f525f2808bd79~mv2.png';
const DEFAULT_RIGHT_IMAGE =
  'https://static.wixstatic.com/media/6ff1e1_90f6726bec9b4cd3a3ad63791dafd4f2~mv2.jpg/v1/fill/w_388,h_388,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/6ff1e1_90f6726bec9b4cd3a3ad63791dafd4f2~mv2.jpg';

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const LeverageSection: React.FC<LeverageSectionProps> = ({
  badge = '善的槓桿',
  title = '一份捐款，兩份改變',
  description = '傳統食物援助只解決「今天吃什麼」；好餸社企讓每份餐膳同時成為 SEN 青年的職場練習場，創造溫飽與自立雙重回饋。',
  leftImageUrl = DEFAULT_LEFT_IMAGE,
  rightImageUrl = DEFAULT_RIGHT_IMAGE,
  leftTitle = '溫飽基層家庭',
  leftDescription = '為有需要家庭提供新鮮熱騰的餐膳，讓每個人都能有尊嚴地吃飽，減輕生活壓力。',
  rightTitle = '培育 SEN 青年自立',
  rightDescription = '在中央廚房與社區飯堂的實戰崗位中，學習餐飲技能、建立自信，走向自立就業。',
  beforeLabel = '傳統慈善：送飯 → 吃飽',
  afterLabel = '好餸社企：送飯 → 吃飽 + 職訓 + 就業',
}) => {
  // EN 模式下以 en/content.json 的 leverage.mirror 覆蓋 props（zh 原樣）
  badge = cms('leverage.mirror.badge', badge);
  title = cms('leverage.mirror.title', title);
  description = cms('leverage.mirror.description', description);
  leftTitle = cms('leverage.mirror.leftTitle', leftTitle);
  leftDescription = cms('leverage.mirror.leftDescription', leftDescription);
  rightTitle = cms('leverage.mirror.rightTitle', rightTitle);
  rightDescription = cms('leverage.mirror.rightDescription', rightDescription);
  beforeLabel = cms('leverage.mirror.beforeLabel', beforeLabel);
  afterLabel = cms('leverage.mirror.afterLabel', afterLabel);
  const { t } = useTranslation();
  return (
    <section className="py-16 md:py-24 px-4 bg-[#FFFBF5]">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeInUp}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-block px-4 py-1.5 bg-[#FFF7ED] text-[#C2410C] text-sm font-bold rounded-full mb-4">
            {badge}
          </span>
          <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4">
            {title}
          </h2>
          <p className="text-stone-600 leading-relaxed">{description}</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="relative rounded-2xl overflow-hidden group"
          >
            <div className="aspect-[4/3]">
              <img
                src={leftImageUrl}
                alt={leftTitle}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
              <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-xs font-bold rounded-full mb-3">
                {t('content:leverage.firstChange')}
              </span>
              <h3 className="text-xl md:text-2xl font-bold mb-2">{leftTitle}</h3>
              <p className="text-white/90 text-sm md:text-base leading-relaxed">
                {leftDescription}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={fadeInUp}
            className="relative rounded-2xl overflow-hidden group"
          >
            <div className="aspect-[4/3]">
              <img
                src={rightImageUrl}
                alt={rightTitle}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white">
              <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-xs font-bold rounded-full mb-3">
                {t('content:leverage.secondChange')}
              </span>
              <h3 className="text-xl md:text-2xl font-bold mb-2">{rightTitle}</h3>
              <p className="text-white/90 text-sm md:text-base leading-relaxed">
                {rightDescription}
              </p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeInUp}
          className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6"
        >
          <div className="px-5 py-3 bg-stone-100 text-stone-600 rounded-full text-sm font-medium">
            {beforeLabel}
          </div>
          <div className="text-[#C2410C]">
            <ArrowRight size={20} className="rotate-90 md:rotate-0" />
          </div>
          <div className="px-5 py-3 bg-[#C2410C] text-white rounded-full text-sm font-bold shadow-md">
            {afterLabel}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LeverageSection;
